import React, { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  Layout,
  PageHeader,
  Button,
  Form,
  Select,
  Input,
  Space,
  Spin,
  message,
  AutoComplete,
  Divider,
  Affix,
  Typography,
  Checkbox,
} from 'antd'
import { MinusCircleOutlined } from '@ant-design/icons'

import HeaderNavigation from '../../components/HeaderNavigation'
import EstimateItemInput from '../../components/estimateItem/EstimateItemInput'

import styles from './style.module.css'
import useCategory from '../../components/hooks/useCategory'
import SISelect from '../../components/SISelect'
import ProviderSelector from '../../components/ProviderSelector'
import SuggestionAutocomplete from '../../components/SuggestionAutocomplete'
import { createApplicationImplementation } from '../../services/application'
import DecimalInput from '../../components/DecimalInput'
import { OFF_BUDGET_OPTIONS } from '../../components/application/app-view-utils'
import VatSelect from '../../components/VatSelect'

const { Option } = Select
const { Content } = Layout
const { Text } = Typography

const paymentOptions = [
  {
    value: 'cashless',
    label: 'Безналичный',
  },
  {
    value: 'cash',
    label: 'Наличный',
  },
  {
    value: 'postponement',
    label: 'Безналичный пост оплата',
  },
  {
    value: 'postcashless',
    label: 'Безналичный пред оплата',
  },
]

const NewAppImplementation = () => {
  let navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()
  const [isCreating, setIsCreating] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState()
  const project = useMemo(() => searchParams.get('project'), [searchParams])
  const [categories] = useCategory(project)

  const onCancel = () => {
    form.resetFields()
    navigate('/applications?state=delivery', { replace: true })
  }

  const onEstimateSelect = (estimate, key) => {
    const fields = form.getFieldsValue()
    if (fields && fields?.applicationItems[key]) {
      fields.applicationItems[key].amount = estimate.amount
      fields.applicationItems[key].unit = estimate.unit
      fields.applicationItems[key].name = estimate.name
      fields.applicationItems[key].category = estimate.category
      fields.applicationItems[key].off_budget = null
      form.setFieldsValue(fields)
    }
  }

  const onBudgetOffChange = (value, key) => {
    if (value) {
      const fields = form.getFieldsValue()

      fields.applicationItems[key] = {
        ...fields.applicationItems[key],
        estimate: null,
        /*category: null,
        amount: null,
        unit: null,
        name: null,*/
      }

      form.setFieldsValue(fields)
    }
  }

  const onFormUpdate = (values) => {
    setIsCreating(true)
    const data = {
      application: {
        project: project,
      },
      payment: paymentMethod,
      items: values?.applicationItems?.map((item) => ({
        name: item.name,
        price: item.price,
        provider: item.provider,
        unit: item.unit,
        amount: item.amount,
        article: item.article,
        description: item.description,
        warehouse: item.warehouse,
        off_budget: item.off_budget,
        vatType: item.vatType,
        vatValue: item.vatValue,
        estimate_field: item?.estimate?.key ? item?.estimate?.key : null,
        category: item?.category,
        is_to_stock: item.isToStock,
        approved: true,
      })),
      delivery: values?.transportation?.map((item) => ({
        from: item.from,
        to: item.to,
        price: item.price,
        vatType: item.vatType,
        vatValue: item.vatValue,
        paymentType: item.paymentType,
        provider: item.provider,
      })),
    }
    createApplicationImplementation(data)
      .then((response) => {
        message.success('Заявка успешно обновлена')
        onCancel()
      })
      .catch((error) => message.error('Ошибка при обновлении заявки'))
      .finally(() => setIsCreating(false))
  }

  return (
    <Layout>
      <HeaderNavigation activeKey="1" />
      <Content className={styles.content}>
        <Affix offsetTop={75}>
          <PageHeader
            ghost={false}
            title="Новая заявка"
            extra={[
              <Button key="1" onClick={() => onCancel()}>
                Отмена
              </Button>,
              <Button key="2" type="primary" onClick={() => form.submit()}>
                Создать заявку
              </Button>,
            ]}
          />
        </Affix>
        <div>
          <div style={{ marginTop: 20 }}>
            <Text strong>Способ оплаты: </Text>
            <Select
              loading={isCreating}
              size="small"
              defaultValue="cashless"
              value={paymentMethod}
              onChange={(value) => setPaymentMethod(value)}
              style={{ minWidth: 200 }}
              options={paymentOptions}
              dropdownMatchSelectWidth={false}
            />
            <Divider plain />
          </div>
          <Spin spinning={isCreating}>
            <Form form={form} className={styles.form} layout="vertical" onFinish={onFormUpdate}>
              <Form.List name="applicationItems">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <div>
                        <Space
                          key={key}
                          style={{ display: 'flex', marginBottom: 8, width: '100%' }}
                          align="baseline"
                          wrap
                        >
                          <Form.Item {...restField} label="Позиция из сметы" name={[name, 'estimate']}>
                            <EstimateItemInput
                              loadOnPress={true}
                              project={project}
                              onEstimateSelect={(estimate) => onEstimateSelect(estimate, index)}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Категория из сметы"
                            name={[name, 'category']}
                            style={{ maxWidth: 250, minWidth: 250 }}
                          >
                            <Select
                              options={categories.map((category) => ({
                                value: category,
                                label: category,
                              }))}
                              dropdownMatchSelectWidth={false}
                              showSearch
                              allowClear
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Вне сметы"
                            name={[name, 'off_budget']}
                            style={{ maxWidth: 250, minWidth: 250 }}
                          >
                            <Select
                              options={OFF_BUDGET_OPTIONS}
                              dropdownMatchSelectWidth={false}
                              onChange={(v) => onBudgetOffChange(v, key)}
                              showSearch
                              allowClear
                            />
                          </Form.Item>
                          <Form.Item {...restField} style={{ minWidth: 100 }} label="Артикул" name={[name, 'article']}>
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            style={{ minWidth: 250 }}
                            label="Наименование КП"
                            name={[name, 'name']}
                            rules={[{ required: true, message: 'Поле обязательно' }]}
                          >
                            <SuggestionAutocomplete />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Количество"
                            name={[name, 'amount']}
                            rules={[{ required: true, message: 'Поле обязательно' }]}
                          >
                            <DecimalInput style={{ maxWidth: 100, minWidth: 100 }} />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Единица измерения"
                            name={[name, 'unit']}
                            style={{ maxWidth: 200, minWidth: 200 }}
                            rules={[{ required: true, message: 'Поле обязательно' }]}
                          >
                            <SISelect />
                          </Form.Item>
                          <Form.Item {...restField} label="Цена за ед." name={[name, 'price']}>
                            <DecimalInput style={{ maxWidth: 100, minWidth: 100 }} />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="НДС"
                            name={[name, 'vatType']}
                            style={{ maxWidth: 150, minWidth: 150 }}
                          >
                            <Select dropdownMatchSelectWidth={false}>
                              <Option value="none">Без НДС</Option>
                              <Option value="up">НДС сверху</Option>
                              <Option value="include">НДС в сумме</Option>
                            </Select>
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="% НДС"
                            name={[name, 'vatValue']}
                            style={{ maxWidth: 100, minWidth: 100 }}
                          >
                            <VatSelect />
                          </Form.Item>
                          <Form.Item {...restField} label="Поставщик" name={[name, 'provider']}>
                            <ProviderSelector />
                          </Form.Item>
                          <Form.Item {...restField} label="Склад" name={[name, 'warehouse']} style={{ minWidth: 250 }}>
                            <AutoComplete
                              options={[{ value: 'Муром, Радиозаводское шоссе, 2Б' }]}
                              dropdownMatchSelectWidth={false}
                            />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'description']} label="Примечание">
                            <Input />
                          </Form.Item>
                          <Form.Item
                            label="Добавить оборудование на склад объекта"
                            name={[name, 'isToStock']}
                            valuePropName="checked"
                            initialValue={false}
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item label=" ">
                            <MinusCircleOutlined onClick={() => remove(name)} />
                          </Form.Item>
                        </Space>
                        <Divider plain />
                      </div>
                    ))}
                    <Form.Item style={{ paddingTop: 15 }}>
                      <Button type="dashed" onClick={() => add()} block>
                        Добавить позицию в заявку
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
              <Form.List name="transportation">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key}>
                        <Space style={{ display: 'flex', width: '100%' }} align="baseline" wrap>
                          <Form.Item {...restField} label="Идентификатор" name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Адрес отправления"
                            name={[name, 'from']}
                            style={{ minWidth: 300 }}
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Адрес доставки"
                            name={[name, 'to']}
                            style={{ minWidth: 300 }}
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item {...restField} label="Стоимость доставки" name={[name, 'price']}>
                            <DecimalInput style={{ maxWidth: 150, minWidth: 150 }} />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="НДС"
                            name={[name, 'vatType']}
                            style={{ maxWidth: 150, minWidth: 150 }}
                          >
                            <Select dropdownMatchSelectWidth={false}>
                              <Option value="none">Без НДС</Option>
                              <Option value="up">НДС сверху</Option>
                              <Option value="include">НДС в сумме</Option>
                            </Select>
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="% НДС"
                            name={[name, 'vatValue']}
                            style={{ maxWidth: 100, minWidth: 100 }}
                          >
                            <VatSelect />
                          </Form.Item>
                          <Form.Item {...restField} label="Способ оплаты" name={[name, 'paymentType']}>
                            <Select defaultValue="cashless" style={{ minWidth: 200 }} options={paymentOptions} />
                          </Form.Item>
                          <Form.Item {...restField} label="Поставщик" name={[name, 'provider']}>
                            <ProviderSelector />
                          </Form.Item>
                          <Form.Item label=" ">
                            <MinusCircleOutlined disabled={isCreating} onClick={() => remove(name)} />
                          </Form.Item>
                        </Space>
                      </div>
                    ))}
                    <Form.Item>
                      <Button disabled={isCreating} type="dashed" onClick={() => add()} block>
                        Добавить доставку
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          </Spin>
        </div>
      </Content>
    </Layout>
  )
}

export default NewAppImplementation
